import { useState } from "react";
import { C } from "../../../lib/tokens.js";
import { useViewport } from "../../../lib/useViewport.js";
import { t } from "../../../lib/data.js";
import { isAgentRole } from "../../../lib/utils.js";
import { useToast } from "../../../lib/ToastContext.jsx";
import { useExtensionSlot } from "../../../lib/ExtensionSlotsContext.jsx";
import { Button } from "../../../components/ui/Button.jsx";
import { Modal } from "../../../components/ui/Modal.jsx";
import { AssigneePicker } from "../../../components/ui/AssigneePicker.jsx";
import { StatusChip, PriorityChip, TypeChip, ModuleChip } from "../../../components/ui/Chips.jsx";
import { useAutoGrowTextarea } from "../../../lib/useAutoGrowTextarea.js";

function EditFieldRow({ label, children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <span style={{ fontSize: 11, color: C.iron, width: 100, flex: "none", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 500 }}>
        {label}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );
}

export function EditRequestModal({ request, role, agents, me, onUpdate, onRefresh, onClose }) {
  const { addToast } = useToast();
  const isAgent = isAgentRole(role);
  const { isMobile } = useViewport();
  const chipSize = isMobile ? "md" : "sm";

  const [summary, setSummary] = useState(request.summary);
  const [status, setStatus] = useState(request.status);
  const [priority, setPriority] = useState(request.priority);
  const [module, setModule] = useState(request.module);
  const [type, setType] = useState(request.type);
  const [assigneeId, setAssigneeId] = useState(request.assigneeId);
  const [note, setNote] = useState("");
  const noteRef = useAutoGrowTextarea(note);
  const [confidentialType, setConfidentialType] = useState("A");
  const [saving, setSaving] = useState(false);
  // Optional module-contributed fieldset + persist (e.g. afsp.afts's invoicing fields),
  // undefined when no module fills them. `extra` is opaque here and null while nothing
  // changed. EditFieldRow is passed down as FieldRow so contributed rows line up.
  const ExtraFields = useExtensionSlot("request.edit.extraFields");
  const persistExtra = useExtensionSlot("request.edit.persistExtra");
  const [extra, setExtra] = useState(null);
  const [extraValid, setExtraValid] = useState(true);

  const assigneeName = agents.find((a) => a.id === assigneeId)?.name;

  async function handleSave() {
    setSaving(true);
    try {
      const statusChanged = status !== request.status;

      const text = note.trim();

      const patch = {};
      if (isAgent && summary.trim() && summary.trim() !== request.summary) patch.summary = summary.trim();
      if (priority !== request.priority) patch.priority = priority;
      if (module !== request.module) patch.module = module;
      if (type !== request.type) patch.type = type;
      if (assigneeId !== request.assigneeId) patch.assigneeId = assigneeId;
      if (statusChanged) patch.status = status;
      if (Object.keys(patch).length > 0 || text) {
        // confidentialType reaches the request itself before the backend save — that's
        // what suppresses the change-notification email when set to Internal, note or not.
        if (isAgent) patch.confidentialType = confidentialType;
        if (text) patch.comment = text;
        await onUpdate?.(request.id, patch);
      }
      if (isAgent && persistExtra && extra != null) await persistExtra(request.id, extra);

      await onRefresh?.(request.id);
      onClose();
    } catch (err) {
      addToast(err.status === 400 ? err.message : "Could not save changes.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onClose} maxHeight="calc(100vh - 64px)">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 15, fontWeight: 500, color: C.deepsea }}>Edit Request</span>
        <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", padding: 4, display: "flex", borderRadius: 4 }}>
          <span className="material-icons" style={{ fontSize: 18, color: C.iron }}>close</span>
        </button>
      </div>

      {isAgent ? (
        <EditFieldRow label="Name">
          <input
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="Request summary"
            maxLength={60}
            style={{
              width: "100%", boxSizing: "border-box", border: `1px solid ${C.mercury}`,
              borderRadius: 4, padding: "6px 8px", fontSize: 13, outline: "none", fontFamily: "Roboto",
              color: C.fg, background: C.tin,
            }}
          />
          <div style={{ fontSize: 11, color: summary.trim().length > 0 && summary.trim().length < 8 ? C.rust : C.iron, marginTop: 2 }}>
            {summary.trim().length}/60 · 8 characters minimum
          </div>
        </EditFieldRow>
      ) : null}

      {isAgent ? (
        <EditFieldRow label="Status">
          <StatusChip id={status} size={chipSize} filled onChange={setStatus} />
        </EditFieldRow>
      ) : null}

      <EditFieldRow label={t("req.detail.priority")}>
        <PriorityChip id={priority} size={chipSize} onChange={setPriority} />
      </EditFieldRow>

      <EditFieldRow label={t("req.detail.module")}>
        <ModuleChip id={module} size={chipSize} onChange={setModule} />
      </EditFieldRow>

      {isAgent ? (
        <>
          <EditFieldRow label={t("req.filter.type")}>
            <TypeChip id={type} size={chipSize} onChange={setType} />
          </EditFieldRow>

          <EditFieldRow label={t("req.detail.asm")}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 8 }}>
              {assigneeId ? (
                <span style={{ fontSize: 12, color: C.fg, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{assigneeName ?? assigneeId}</span>
              ) : (
                <span style={{ fontSize: 12, color: C.silver, fontStyle: "italic" }}>Unassigned</span>
              )}
              <AssigneePicker agents={agents} currentId={assigneeId} onSelect={setAssigneeId} meId={me?.id} />
            </div>
          </EditFieldRow>

          {ExtraFields && (
            <ExtraFields request={request} me={me} FieldRow={EditFieldRow} onChange={setExtra} onValidChange={setExtraValid} />
          )}
        </>
      ) : null}

      <div style={{ height: 1, background: C.mercury }} />

      <div style={{ fontSize: 13, color: C.iron }}>Add a note about this change (optional)</div>
      <textarea
        ref={noteRef}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={1}
        placeholder="What changed and why…"
        style={{
          fontFamily: "Roboto", fontSize: 13, padding: "8px 10px", border: `1px solid ${C.mercury}`,
          borderRadius: 4, outline: "none", resize: "none", overflowY: "auto", overflowX: "hidden",
          minHeight: 64, maxHeight: 300, lineHeight: 1.5,
          color: C.fg, background: C.platinum, boxSizing: "border-box", width: "100%",
        }}
      />

      {isAgent ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: C.iron }}>Visibility</span>
          <select
            value={confidentialType}
            onChange={(e) => setConfidentialType(e.target.value)}
            style={{
              fontFamily: "Roboto", fontSize: 12, padding: "5px 8px", borderRadius: 4,
              border: `1px solid ${C.mercury}`, background: C.platinum, color: C.fg, outline: "none",
              cursor: "pointer",
            }}
          >
            <option value="A">Public Information</option>
            <option value="C">Partner Confidential</option>
            <option value="I">Internal</option>
          </select>
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Button kind="outline" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button kind="primary" onClick={handleSave} disabled={saving || !extraValid || (isAgent && (!summary.trim() || summary.trim().length < 8))}>{saving ? "Saving…" : "Save changes"}</Button>
      </div>
    </Modal>
  );
}
